import { Component } from 'react';
import axios from 'axios';
import { config } from '../config/config';
import { createSignature } from '../utils/encrypt-functions/create-signature';
// import {getTestData} from "../utils/helper-functions/get-test-data";
import { delay } from '../utils/helper-functions/delay';
import { getUnixTimestamp } from '../utils/helper-functions/get-unix-timestamp';
import store from '../state/store';
import { perfBreak } from '../utils/performance-functions/perf-break';
import { adler } from '../utils/encrypt-functions/adler32-encrypt';
import { getDeviceData } from '../utils/helper-functions/get-device-data';
import { getTokenFromUrl } from '../utils/helper-functions/getTokenFromUrl';
import { prodTheme } from '../variables/prod-theme';
import history from '../variables/history';
import { isChineseDomain } from '../variables/is-chinese-domain';
import { getUrl, isTest } from '../variables/host';
import { sendOpenSearchLogs } from 'utils/helper-functions/send-open-search-logs.ts';
import { isProd } from '../variables/app-variables';
import { graphQlQueryMapping } from '../graphql-apollo/queries/index';
import { createApolloClient } from '../graphql-apollo/apollo';
import { ResponseStatus } from '../variables/response-status';
import { getApolloQuery } from '../graphql-apollo/apollo.ts';
import { isPanoramicTheme } from '@pano-project/variables/is-panoramic-2-0-theme';
import { initialDataPrefetch } from '../shared-components/wp-components/initial-data/initial-data-prefetch';
import {
  mapGetUserGraphQlData,
  mapGetSessionTransactionGraphQlData,
  mapGetGatewaysGraphQlData,
  mapDynamicOptionsGraphQLToRestPayload,
  mapGetGatewayDetailsGraphQlData,
  mapGetGatewaysPanoramicGraphQlData,
  mapGetGatewayDetailsPanoramicGraphQlData,
  mapGetWithdrawalRequestsGraphQlData,
} from '@pano-project/utils/graphqlDataMapping';

let getTestData = () => {};
let graphqlMockRequest = () => {};

if (!isProd) {
  getTestData = require('../utils/helper-functions/get-test-data').getTestData;
  graphqlMockRequest = require('../utils/helper-functions/get-test-data').graphqlMockRequest;
}

const testTheme = (token) =>
  isProd ? prodTheme : getTestData('get-asset-data', is_documentation(token));

const is_documentation = (token) => {
  return (
    token === 'f3fd281d1b8af07e9ccb4c9c80dfc3ba58b10c9a-1' &&
    config.api === 'https://hermes.cashier-dev.com/api/'
  );
};

const current_api = getUrl('api');
const current_apiTheme = isChineseDomain
  ? 'https://compute.prx-pay.com/hermes-assets/'
  : getUrl('apiTheme');

const getBasicParams = () => {
  const {
    token,
    initTheme: { communication_token, session_duration, session_theme },
  } = store.getState();
  const timestamp = getUnixTimestamp(new Date().getTime());
  return {
    token,
    communication_token,
    session_duration,
    session_theme,
    timestamp,
  };
};

let time_info = {};
const cacheData = {};
let loading_time = 0;
let restarted = false;

export const CLIENT = createApolloClient('graphqlDomain');

export default class PraxisService extends Component {
  timeInfoUpdate = (arg = {}) => {
    if (!arg || typeof arg !== 'object') return;
    time_info = { ...time_info, ...arg };
  };

  getCompute = async (token) => {
    loading_time = perfBreak();
    if (isTest || store.getState().location.test === '1' || config.skipComputeRequest) {
      console.log('request', 'get-compute', { token }, 'response', testTheme(token));
      const res = await delay(testTheme(token));
      return res;
    }

    let options = {
      method: 'get',
      url: `${current_apiTheme}${token}`,
      data: { token },
    };

    let res;
    try {
      res = await axios(options);
    } catch (e) {
      sendOpenSearchLogs({
        event_group: 'cashier_get_compute',
        error_data: e,
      });
      const { name, message } = e;
      this.postComputeLogs({ name, message }).catch((err) =>
        console.error('Error logging compute success:', err),
      );
      return;
    }

    const { data } = res;

    this.postComputeLogs(data).catch((err) => console.error('Error logging compute success:', err));
    return res;
  };

  cashierAxios = async (url, data, delayTime, cache, method = 'post') => {
    let cacheKey;
    if (cache) cacheKey = adler(url + JSON.stringify(data));

    data = { ...getBasicParams(), ...data };
    if (isTest) {
      return await this.fetchTestData({
        data,
        action: url,
        requestType: 'https',
        cache,
        cacheKey,
        delayTime,
      });
    }

    if (isTest) {
      const { option_id } = data;
      console.log(
        'https-request',
        url,
        data,
        'https-response',
        getTestData(url, option_id && { option_id }),
      );
      if (cache && cacheData[cacheKey]) return cacheData[cacheKey];
      const res = await delay(getTestData(url, data), delayTime);
      if (cache) cacheData[cacheKey] = res;
      return res;
    }

    let options = {
      method,
      url: `${current_api}cashier/${url}`,
      data: { ...data, signature: createSignature(data) },
    };

    if (cache && cacheData[cacheKey]) return cacheData[cacheKey];
    const res = await axios(options);
    if (cache) cacheData[cacheKey] = res;
    return res;
  };

  fetchTestData = async ({
    data = {},
    action,
    requestType = '',
    cache,
    cacheKey,
    delayTime = 0,
  }) => {
    const { option_id } = data;
    console.log(
      `${requestType}-request`,
      action,
      data,
      `${requestType}-response`,
      getTestData(action, option_id && { option_id }),
    );
    if (cache && cacheData[cacheKey]) return cacheData[cacheKey];
    const res = await delay(getTestData(action, data), delayTime);
    if (cache) cacheData[cacheKey] = res;
    return res;
  };

  cashierRequest = async (url = '', data = {}, delayTime = 0, cache = false, method = 'post') => {
    return this.cashierAxios(url, data, delayTime, cache, method);
  };

  getServerOptions = async (data) => {
    if (isPanoramicTheme) {
      const {
        option_id,
        option_type,
        amount,
        currency,
        dynamic_field_name,
        country: _country,
      } = data;

      const parsedAmount =
        typeof amount === 'number' ? amount : parseFloat(String(amount ?? '').replace(',', '.'));
      const variables = {
        option_id,
        option_type: option_type || 'payment_method',
        amount: Number.isFinite(parsedAmount) ? parsedAmount : 0,
        currency,
        dynamic_field_name,
      };
      const payload = await this.getGraphQLData({
        queryName: 'get_dynamic_options',
        mapGraphQLDataToAppData: mapDynamicOptionsGraphQLToRestPayload,
        setErrorGraphQL: () => {},
        variables,
      });
      if (!payload) {
        throw new Error('get_dynamic_options failed');
      }
      return { data: payload };
    } else {
      return this.cashierAxios('get-dynamic-options', data, 2000, true);
    }
  };

  serverDeleteTransaction = (trace_id) => this.cashierRequest('delete-transaction', { trace_id });

  postErrors = (data) => {
    sendOpenSearchLogs({
      error_data: data,
    });

    return this.cashierAxios('errors', data);
  };

  saveData = (data) => this.cashierRequest('save-data', data);

  postLoadingTime = (loading_time) => {
    const data = { loading_time: `${loading_time}`, time_info };
    return this.cashierRequest('long-query', data);
  };

  postPayGateway = (data) => {
    const {
      gatewayInfo: { method },
    } = store.getState();
    const device_data = method === 'card-method' ? getDeviceData() : null;
    return this.cashierRequest('pay-with-gateway', { ...data, device_data });
  };

  getAvailableGateways = async (data = {}) => {
    const {
      payInfo: { pciPraxisGate },
    } = store.getState();
    const { getAvailableGateways } = pciPraxisGate;
    if (isTest)
      return await this.fetchTestData({
        action: 'get-processing-gateway-list',
        data,
        requestType: 'sdk',
      });
    return await getAvailableGateways({ ...data });
  };

  getOBTMethods = async (data) => {
    if (isTest)
      return await this.fetchTestData({
        action: 'get-available-obt-methods',
        data: {},
        requestType: 'sdk',
      });

    const {
      payInfo: { pciPraxisGate },
    } = store.getState();
    const { getAvailableOBTMethods } = pciPraxisGate;
    return await getAvailableOBTMethods(data);
  };

  getGateways = async (
    type,
    ob_decline_recovery = 0,
    retry_with_apm = 0,
    last_used_method_first = null,
    first_gateway_details = false,
  ) => {
    const isPayOut = type === 'pay-out';

    let url = isPayOut
      ? `get-gateway-list-without-details`
      : 'get-payment-method-list-without-details';

    const start = perfBreak();

    const {
      initTheme: {
        theme_editor_settings: {
          customization_settings: { layout, currency_display_mode } = {},
        } = {},
        applicationConfiguration = {},
        sessionInformation,
      } = {},
      payData: { currency: payDataCurrency } = {},
      profileInfo: { base_currency } = {},
    } = store.getState();

    const isAmountFirstLayout = layout === 'amount-first';
    const selectedCurrency = payDataCurrency || base_currency || null;
    const currencyDisplayMode = currency_display_mode;
    const shouldSendCurrency =
      isAmountFirstLayout && currencyDisplayMode !== 'base_currency' && !!selectedCurrency;

    if (isPanoramicTheme) {
      const getGatewaysListQueryNameBasedOnLayout = (layout) => {
        switch (layout) {
          case 'amount-first':
            return 'get_payment_method_groups';
          default:
            return 'get_payment_method_groups_panoramic';
        }
      };

      const getMappingFunctionBasedOnLayout = (layout) => {
        switch (layout) {
          case 'amount-first':
            return mapGetGatewaysGraphQlData;
          default:
            return mapGetGatewaysPanoramicGraphQlData;
        }
      };

      const currency =
        (selectedCurrency ? selectedCurrency : sessionInformation?.charge_currency) || null;
      let decline_recovery_type = null;
      if (ob_decline_recovery) {
        decline_recovery_type = 'ob_decline_recovery';
      }
      if (retry_with_apm) {
        decline_recovery_type = 'apm_decline_recovery';
      }
      const variables = {
        ...(shouldSendCurrency && { currency: selectedCurrency }),
        sort: last_used_method_first ? 'last_used_method_first' : null,
        format: isAmountFirstLayout ? 'grouped_list' : 'grouped',
        decline_recovery_type: decline_recovery_type,
        skipFirstGateway: !first_gateway_details,
      };

      try {
        const prefetched = initialDataPrefetch.take('gateways', {
          format: variables.format,
          sort: variables.sort,
          decline_recovery_type: variables.decline_recovery_type,
        });

        if (prefetched) {
          const prefetchedGateways = getMappingFunctionBasedOnLayout(layout)(
            first_gateway_details ? prefetched : { ...prefetched, first_gateway_details: null },
          );
          this.timeInfoUpdate({ getPaymentMethodList: (perfBreak() - start).toFixed(3) });
          return { data: { ...prefetchedGateways, status: prefetchedGateways?.status ?? 0 } };
        }

        const graphQlGateways = await this.getGraphQLData({
          queryName: getGatewaysListQueryNameBasedOnLayout(layout),
          mapGraphQLDataToAppData: getMappingFunctionBasedOnLayout(layout),
          setErrorGraphQL: () => {},
          variables,
        });

        const end = (perfBreak() - start).toFixed(3);
        this.timeInfoUpdate({ getPaymentMethodList: end });

        return {
          data: {
            ...graphQlGateways,
            status: graphQlGateways?.status ?? 0,
          },
        };
      } catch (error) {
        const errorText = 'Cant get payment methods';

        sendOpenSearchLogs({
          event_group: 'cashier_graphql_get_payment_methods',
          error_data: {
            error: {
              message: errorText,
              stack: error,
            },
          },
        });
        console.error(errorText, error);
      }
    }

    const res = await this.cashierRequest(url, {
      ob_decline_recovery,
      apm_decline_recovery: retry_with_apm,
      first_gateway_details,
      ...(last_used_method_first && { sort: 'last_used_method_first' }),
    });
    const end = (perfBreak() - start).toFixed(3);
    this.timeInfoUpdate({ getPaymentMethodList: end });

    loading_time = (perfBreak() - loading_time).toFixed(3);
    if (loading_time > 8) this.postLoadingTime(loading_time);

    if (!isPayOut) {
      const {
        gateways,
        is_new_window_mobile,
        is_bdcc_enabled,
        is_bdcc_window,
        cvv_input_as_buttons,
        is_encrypted_input_names,
        send_get_available_gateways_request,
      } = res.data;

      sendOpenSearchLogs({
        event_group: 'cashier_initialization',
        event_name: 'get_available_payment_methods',
        event_data: {
          payment_methods: gateways?.map((gate) => {
            const {
              image,
              image_dark_mode,
              title,
              option_id,
              option_type,
              payment_method_type,
              payment_method_key,
            } = gate;

            return {
              image,
              image_dark_mode,
              title,
              option_id,
              option_type,
              payment_method_type,
              payment_method_key,
            };
          }),
          is_new_window_mobile,
          is_bdcc_enabled,
          is_bdcc_window,
          cvv_input_as_buttons,
          is_encrypted_input_names,
          send_get_available_gateways_request,
        },
        event_context: {
          response_code: res.data.status,
          http_status_code: res.status,
        },
      });
    }

    return res;
  };

  getGatewayDetails = async (data = {}) => {
    const {
      initTheme: { theme_editor_settings: { customization_settings: { layout } = {} } = {} } = {},
      gatewaysList: { gateways } = {},
    } = store.getState();

    const { option_type, option_id, payment_information } = data;
    let params = { option_type, option_id };

    const getGatewayDetailsQueryNameBasedOnLayout = (layout) => {
      switch (layout) {
        case 'amount-first':
          return 'get_gateway_details';
        default:
          return 'get_gateway_details_panoramic';
      }
    };

    const getMappingFunctionBasedOnLayout = (layout) => {
      switch (layout) {
        case 'amount-first':
          return mapGetGatewayDetailsGraphQlData;
        default:
          return mapGetGatewayDetailsPanoramicGraphQlData;
      }
    };

    const mappingFunction = getMappingFunctionBasedOnLayout(layout);

    if (isPanoramicTheme) {
      const gatewayData = await this.getGraphQLData({
        queryName: getGatewayDetailsQueryNameBasedOnLayout(layout),
        mapGraphQLDataToAppData: (graphQLGateway) =>
          mappingFunction({ ...data, graphQLGateway, gateways }),
        setErrorGraphQL: () => {},
        variables: {
          option_id: data.option_id,
          option_type: data.option_type,
        },
      });
      return { data: gatewayData };
    } else {
      return await this.cashierRequest('get-gateway-details', { ...params }, 3000);
    }
  };

  postComputeLogs = (response = {}) => {
    const { token, timestamp } = getBasicParams();
    const request = { token };
    const { communication_token, session_duration, session_theme } = response;
    const compute_url = current_apiTheme;
    const data = {
      request,
      response,
      compute_url,
      timestamp,
      token,
      communication_token,
      session_duration,
      session_theme,
    };
    return this.cashierRequest('compute-request', data);
  };

  getUser = async (callback) => {
    const {
      initTheme: {
        theme_editor_settings: {
          customization_settings: {
            quick_amount_options,
            promocode_configuration,
            layout,
            promocode,
          } = {},
        },
        sessionInformation,
        applicationConfiguration = {},
      },
      profileInfo: { transaction_type },
    } = store.getState();

    const promocodeListEnabled =
      promocode_configuration?.type === 'bonus_list' && promocode_configuration?.enabled;
    const prevDepositQuickAmount = quick_amount_options?.find(
      (option) => option.type === 'previous_deposit_amount',
    )?.enabled;
    const isAmountFirstLayout = layout === 'amount-first';

    const start = perfBreak();

    if (isPanoramicTheme) {
      // If you want to test the session transaction in local version, you can uncomment the next line
      // if (!isProd) callback = 1;

      const geCustomerQueryNameBasedOnLayout = (layout) => {
        switch (layout) {
          case 'amount-first':
            return 'get_customer_currencies_transaction_promocodes_data';
          default:
            return 'get_customer_last_transaction_promocodes_data';
        }
      };

      const geTransactionQueryNameBasedOnLayout = (layout) => {
        switch (layout) {
          case 'amount-first':
            return 'get_session_transaction';
          default:
            return 'get_transaction';
        }
      };

      try {
        const queryName = callback
          ? geTransactionQueryNameBasedOnLayout(layout)
          : geCustomerQueryNameBasedOnLayout(layout);
        const mapFunc = callback ? mapGetSessionTransactionGraphQlData : mapGetUserGraphQlData;

        if (!callback) {
          const prefetched = initialDataPrefetch.take('customer');
          if (prefetched) {
            const mapped = mapGetUserGraphQlData(prefetched);
            this.timeInfoUpdate({ getCustomerData: (perfBreak() - start).toFixed(3) });
            return { data: { ...mapped, status: mapped.status ?? 0 } };
          }
        }

        const data = await this.getGraphQLData({
          queryName,
          mapGraphQLDataToAppData: mapFunc,
          setErrorGraphQL: () => {},
          ...(!isAmountFirstLayout && {
            variables: {
              fetchLastTransaction: prevDepositQuickAmount ?? false,
              fetchPromoCodes: promocodeListEnabled ?? false,
            },
          }),
        });

        const end = (perfBreak() - start).toFixed(3);
        this.timeInfoUpdate({ getCustomerData: end });

        return {
          data: {
            ...data,
            status: data.status ?? 0,
          },
        };
      } catch (error) {
        const errorText = 'Cant get customer data';

        sendOpenSearchLogs({
          event_group: `cashier_graphql_${callback ? geTransactionQueryNameBasedOnLayout(layout) : geCustomerQueryNameBasedOnLayout(layout)}`,
          error_data: {
            error: {
              message: errorText,
              stack: error,
            },
          },
        });
        console.error(errorText, error);
      }
    } else {
      const res = await this.cashierRequest('get-customer-data', {
        callback,
        ...(prevDepositQuickAmount && {
          include_last_transaction: 'approved',
        }),
        ...(promocodeListEnabled && {
          bonus_list: promocodeListEnabled ? 1 : 0,
        }),
      });
      const end = (perfBreak() - start).toFixed(3);
      this.timeInfoUpdate({ getCustomerData: end });
      return res;
    }
  };

  getTransactionStatus = (data = {}) => this.cashierRequest('get-transaction-status', data);

  partialApprovedResult = (data) => this.cashierRequest('partial-approve', data, 3000);

  manageRetryRequest = (data) => this.cashierRequest('manage-retry-request', data, 1000);

  serverGetTransactions = async () => {
    if (isPanoramicTheme) {
      const prefetched = initialDataPrefetch.take('withdrawals');
      if (prefetched) {
        return {
          data: {
            withdrawal_requests: mapGetWithdrawalRequestsGraphQlData(prefetched),
          },
        };
      }

      const withdrawalRequests = await this.getGraphQLData({
        queryName: 'get_withdrawal_requests',
        mapGraphQLDataToAppData: mapGetWithdrawalRequestsGraphQlData,
        setErrorGraphQL: () => {},
        variables: {},
      });

      return {
        data: {
          withdrawal_requests: withdrawalRequests,
        },
      };
    } else {
      return this.cashierRequest('get-transactions');
    }
  };

  getGraphQLData = async ({ queryName, mapGraphQLDataToAppData, setErrorGraphQL, variables }) => {
    try {
      if (isTest) {
        setErrorGraphQL(false);
        const testGraphData = await graphqlMockRequest(undefined, 300, queryName, variables);
        return mapGraphQLDataToAppData(testGraphData?.data);
      }

      const query = graphQlQueryMapping[queryName];

      if (!query) throw new Error('graphql query do not exist: ', queryName);

      const data = await getApolloQuery({
        query,
        client: CLIENT,
        variables: variables,
        history: history,
      });

      if (data === null) {
        setErrorGraphQL(true);
        return {};
      }
      return mapGraphQLDataToAppData(data);
    } catch (err) {
      const errorText = `Cant get ${queryName}`;

      setErrorGraphQL(true);

      sendOpenSearchLogs({
        event_group: `cashier_graphql_${queryName}`,
        error_data: {
          error: {
            message: errorText,
            stack: err,
          },
        },
      });
      console.error(errorText, err);
    }
  };

  manageTemplate = ({ template_token, template_type, action }) => {
    return this.cashierRequest('manage-template', { template_token, template_type, action });
  };
}
